export type GearCategory =
  | 'ammunition'
  | 'arcane-focus'
  | 'druidic-focus'
  | 'holy-symbol'
  | 'container'
  | 'standard-gear';

export interface AdventuringGear {
  id: string;
  name: string;
  category: GearCategory;
  cost: { amount: number; unit: 'cp' | 'sp' | 'gp' };
  weight: number;
  description?: string;
  capacity?: string; // For containers
  quantity?: number; // Default quantity (e.g., arrows come in 20)
}

/**
 * SRD 5.1 Adventuring Gear
 */
export const adventuringGear: AdventuringGear[] = [
  // Ammunition
  {
    id: 'arrows',
    name: 'Arrows',
    category: 'ammunition',
    cost: { amount: 1, unit: 'gp' },
    weight: 1,
    quantity: 20,
    description: 'A quiver of 20 arrows.',
  },
  {
    id: 'blowgun-needles',
    name: 'Blowgun Needles',
    category: 'ammunition',
    cost: { amount: 1, unit: 'gp' },
    weight: 1,
    quantity: 50,
    description: 'A case of 50 blowgun needles.',
  },
  {
    id: 'crossbow-bolts',
    name: 'Crossbow Bolts',
    category: 'ammunition',
    cost: { amount: 1, unit: 'gp' },
    weight: 1.5,
    quantity: 20,
    description: 'A case of 20 crossbow bolts.',
  },
  {
    id: 'sling-bullets',
    name: 'Sling Bullets',
    category: 'ammunition',
    cost: { amount: 4, unit: 'cp' },
    weight: 1.5,
    quantity: 20,
    description: 'A pouch of 20 sling bullets.',
  },

  // Arcane Focuses
  {
    id: 'crystal',
    name: 'Crystal',
    category: 'arcane-focus',
    cost: { amount: 10, unit: 'gp' },
    weight: 1,
    description: 'An arcane focus in the form of a crystal.',
  },
  {
    id: 'orb',
    name: 'Orb',
    category: 'arcane-focus',
    cost: { amount: 20, unit: 'gp' },
    weight: 3,
    description: 'An arcane focus in the form of an orb.',
  },
  {
    id: 'rod',
    name: 'Rod',
    category: 'arcane-focus',
    cost: { amount: 10, unit: 'gp' },
    weight: 2,
    description: 'An arcane focus in the form of a rod.',
  },
  {
    id: 'staff',
    name: 'Staff',
    category: 'arcane-focus',
    cost: { amount: 5, unit: 'gp' },
    weight: 4,
    description: 'An arcane focus in the form of a wooden staff.',
  },
  {
    id: 'wand',
    name: 'Wand',
    category: 'arcane-focus',
    cost: { amount: 10, unit: 'gp' },
    weight: 1,
    description: 'An arcane focus in the form of a wand.',
  },

  // Druidic Focuses
  {
    id: 'sprig-of-mistletoe',
    name: 'Sprig of Mistletoe',
    category: 'druidic-focus',
    cost: { amount: 1, unit: 'gp' },
    weight: 0,
    description: 'A druidic focus.',
  },
  {
    id: 'totem',
    name: 'Totem',
    category: 'druidic-focus',
    cost: { amount: 1, unit: 'gp' },
    weight: 0,
    description: 'A druidic focus carved from wood or bone.',
  },
  {
    id: 'wooden-staff',
    name: 'Wooden Staff',
    category: 'druidic-focus',
    cost: { amount: 5, unit: 'gp' },
    weight: 4,
    description: 'A druidic focus in the form of a wooden staff.',
  },
  {
    id: 'yew-wand',
    name: 'Yew Wand',
    category: 'druidic-focus',
    cost: { amount: 10, unit: 'gp' },
    weight: 1,
    description: 'A druidic focus carved from yew wood.',
  },

  // Holy Symbols
  {
    id: 'amulet',
    name: 'Amulet',
    category: 'holy-symbol',
    cost: { amount: 5, unit: 'gp' },
    weight: 1,
    description: 'A holy symbol in the form of an amulet.',
  },
  {
    id: 'emblem',
    name: 'Emblem',
    category: 'holy-symbol',
    cost: { amount: 5, unit: 'gp' },
    weight: 0,
    description: 'A holy symbol embossed or engraved on a shield or worn on clothing.',
  },
  {
    id: 'reliquary',
    name: 'Reliquary',
    category: 'holy-symbol',
    cost: { amount: 5, unit: 'gp' },
    weight: 2,
    description: 'A holy symbol in the form of a small reliquary box.',
  },

  // Containers
  {
    id: 'backpack',
    name: 'Backpack',
    category: 'container',
    cost: { amount: 2, unit: 'gp' },
    weight: 5,
    capacity: '1 cubic foot / 30 pounds of gear',
    description: 'A leather pack with straps.',
  },
  {
    id: 'barrel',
    name: 'Barrel',
    category: 'container',
    cost: { amount: 2, unit: 'gp' },
    weight: 70,
    capacity: '40 gallons liquid / 4 cubic feet solid',
  },
  {
    id: 'basket',
    name: 'Basket',
    category: 'container',
    cost: { amount: 4, unit: 'sp' },
    weight: 2,
    capacity: '2 cubic feet / 40 pounds of gear',
  },
  {
    id: 'bedroll',
    name: 'Bedroll',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 7,
  },
  {
    id: 'bell',
    name: 'Bell',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 0,
  },
  {
    id: 'blanket',
    name: 'Blanket',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'sp' },
    weight: 3,
  },
  {
    id: 'block-and-tackle',
    name: 'Block and Tackle',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 5,
    description: 'A set of pulleys with a cable threaded through them.',
  },
  {
    id: 'book',
    name: 'Book',
    category: 'standard-gear',
    cost: { amount: 25, unit: 'gp' },
    weight: 5,
    description: 'A blank journal or reference book.',
  },
  {
    id: 'bottle-glass',
    name: 'Bottle, Glass',
    category: 'container',
    cost: { amount: 2, unit: 'gp' },
    weight: 2,
    capacity: '1.5 pints',
  },
  {
    id: 'bucket',
    name: 'Bucket',
    category: 'container',
    cost: { amount: 5, unit: 'cp' },
    weight: 2,
    capacity: '3 gallons liquid / 0.5 cubic foot solid',
  },
  {
    id: 'caltrops',
    name: 'Caltrops',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 2,
    quantity: 20,
    description: 'A bag of 20 caltrops that can cover a 5-foot square area.',
  },
  {
    id: 'candle',
    name: 'Candle',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'cp' },
    weight: 0,
    description: 'A candle sheds bright light in a 5-foot radius and dim light for an additional 5 feet for 1 hour.',
  },
  {
    id: 'case-crossbow-bolt',
    name: 'Case, Crossbow Bolt',
    category: 'container',
    cost: { amount: 1, unit: 'gp' },
    weight: 1,
    capacity: '20 crossbow bolts',
  },
  {
    id: 'case-map-scroll',
    name: 'Case, Map or Scroll',
    category: 'container',
    cost: { amount: 1, unit: 'gp' },
    weight: 1,
    capacity: '10 rolled-up sheets of paper or 5 rolled-up sheets of parchment',
  },
  {
    id: 'chain',
    name: 'Chain (10 feet)',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 10,
    description: 'A chain has 10 hit points. It can be burst with a successful DC 20 Strength check.',
  },
  {
    id: 'chalk',
    name: 'Chalk (1 piece)',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'cp' },
    weight: 0,
  },
  {
    id: 'chest',
    name: 'Chest',
    category: 'container',
    cost: { amount: 5, unit: 'gp' },
    weight: 25,
    capacity: '12 cubic feet / 300 pounds of gear',
  },
  {
    id: 'climbers-kit',
    name: "Climber's Kit",
    category: 'standard-gear',
    cost: { amount: 25, unit: 'gp' },
    weight: 12,
    description: 'Includes pitons, boot tips, gloves, and a harness. Allows anchoring for climbing.',
  },
  {
    id: 'clothes-common',
    name: 'Clothes, Common',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'sp' },
    weight: 3,
  },
  {
    id: 'clothes-costume',
    name: 'Clothes, Costume',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 4,
  },
  {
    id: 'clothes-fine',
    name: 'Clothes, Fine',
    category: 'standard-gear',
    cost: { amount: 15, unit: 'gp' },
    weight: 6,
  },
  {
    id: 'clothes-travelers',
    name: "Clothes, Traveler's",
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 4,
  },
  {
    id: 'component-pouch',
    name: 'Component Pouch',
    category: 'standard-gear',
    cost: { amount: 25, unit: 'gp' },
    weight: 2,
    description: 'A small, watertight leather belt pouch with compartments for spell components.',
  },
  {
    id: 'crowbar',
    name: 'Crowbar',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 5,
    description: 'Grants advantage on Strength checks when leverage can be applied.',
  },
  {
    id: 'fishing-tackle',
    name: 'Fishing Tackle',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 4,
    description: 'Includes a wooden rod, silken line, corkwood bobbers, steel hooks, lead sinkers, velvet lures, and narrow netting.',
  },
  {
    id: 'flask',
    name: 'Flask or Tankard',
    category: 'container',
    cost: { amount: 2, unit: 'cp' },
    weight: 1,
    capacity: '1 pint',
  },
  {
    id: 'grappling-hook',
    name: 'Grappling Hook',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 4,
  },
  {
    id: 'hammer',
    name: 'Hammer',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 3,
  },
  {
    id: 'hammer-sledge',
    name: 'Hammer, Sledge',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 10,
  },
  {
    id: 'healers-kit',
    name: "Healer's Kit",
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 3,
    description: 'Has 10 uses. Can stabilize a creature at 0 hit points without a Wisdom (Medicine) check.',
  },
  {
    id: 'holy-water',
    name: 'Holy Water (flask)',
    category: 'standard-gear',
    cost: { amount: 25, unit: 'gp' },
    weight: 1,
    description: 'A creature splashed with holy water or hit by it takes 2d6 radiant damage if it is a fiend or undead.',
  },
  {
    id: 'hourglass',
    name: 'Hourglass',
    category: 'standard-gear',
    cost: { amount: 25, unit: 'gp' },
    weight: 1,
  },
  {
    id: 'hunting-trap',
    name: 'Hunting Trap',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 25,
    description: 'A creature stepping on the trap must make a DC 13 Dexterity saving throw or take 1d4 piercing damage and stop moving.',
  },
  {
    id: 'ink',
    name: 'Ink (1 ounce bottle)',
    category: 'standard-gear',
    cost: { amount: 10, unit: 'gp' },
    weight: 0,
  },
  {
    id: 'ink-pen',
    name: 'Ink Pen',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'cp' },
    weight: 0,
  },
  {
    id: 'jug',
    name: 'Jug or Pitcher',
    category: 'container',
    cost: { amount: 2, unit: 'cp' },
    weight: 4,
    capacity: '1 gallon',
  },
  {
    id: 'ladder',
    name: 'Ladder (10-foot)',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'sp' },
    weight: 25,
  },
  {
    id: 'lamp',
    name: 'Lamp',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'sp' },
    weight: 1,
    description: 'Casts bright light in a 15-foot radius and dim light for an additional 30 feet. Burns for 6 hours on a pint of oil.',
  },
  {
    id: 'lantern-bullseye',
    name: 'Lantern, Bullseye',
    category: 'standard-gear',
    cost: { amount: 10, unit: 'gp' },
    weight: 2,
    description: 'Casts bright light in a 60-foot cone and dim light for an additional 60 feet. Burns for 6 hours on a pint of oil.',
  },
  {
    id: 'lantern-hooded',
    name: 'Lantern, Hooded',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 2,
    description: 'Casts bright light in a 30-foot radius and dim light for an additional 30 feet. Can be dimmed to 5-foot radius.',
  },
  {
    id: 'lock',
    name: 'Lock',
    category: 'standard-gear',
    cost: { amount: 10, unit: 'gp' },
    weight: 1,
    description: 'Comes with a key. Without the key, a DC 15 Dexterity check with thieves\' tools is required to pick.',
  },
  {
    id: 'magnifying-glass',
    name: 'Magnifying Glass',
    category: 'standard-gear',
    cost: { amount: 100, unit: 'gp' },
    weight: 0,
    description: 'Grants advantage on any ability check to appraise or inspect an item that is small or highly detailed.',
  },
  {
    id: 'manacles',
    name: 'Manacles',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 6,
    description: 'DC 20 Dexterity check to escape. DC 20 Strength check to break. Comes with a key.',
  },
  {
    id: 'mess-kit',
    name: 'Mess Kit',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'sp' },
    weight: 1,
    description: 'A tin box containing a cup, cutlery, a plate, and a collapsible pot.',
  },
  {
    id: 'mirror-steel',
    name: 'Mirror, Steel',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 0.5,
  },
  {
    id: 'oil',
    name: 'Oil (flask)',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'sp' },
    weight: 1,
    description: 'Covers a 5-foot square area. If lit, burns for 2 rounds dealing 5 fire damage.',
  },
  {
    id: 'paper',
    name: 'Paper (one sheet)',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'sp' },
    weight: 0,
  },
  {
    id: 'parchment',
    name: 'Parchment (one sheet)',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'sp' },
    weight: 0,
  },
  {
    id: 'perfume',
    name: 'Perfume (vial)',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 0,
  },
  {
    id: 'pick-miners',
    name: "Pick, Miner's",
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 10,
  },
  {
    id: 'piton',
    name: 'Piton',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'cp' },
    weight: 0.25,
  },
  {
    id: 'poison-basic',
    name: 'Poison, Basic (vial)',
    category: 'standard-gear',
    cost: { amount: 100, unit: 'gp' },
    weight: 0,
    description: 'Can coat one weapon or up to 3 pieces of ammunition. Target must succeed on a DC 10 Constitution save or take 1d4 poison damage.',
  },
  {
    id: 'pole',
    name: 'Pole (10-foot)',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'cp' },
    weight: 7,
  },
  {
    id: 'pot-iron',
    name: 'Pot, Iron',
    category: 'container',
    cost: { amount: 2, unit: 'gp' },
    weight: 10,
    capacity: '1 gallon',
  },
  {
    id: 'potion-of-healing',
    name: 'Potion of Healing',
    category: 'standard-gear',
    cost: { amount: 50, unit: 'gp' },
    weight: 0.5,
    description: 'A creature that drinks this potion regains 2d4 + 2 hit points.',
  },
  {
    id: 'pouch',
    name: 'Pouch',
    category: 'container',
    cost: { amount: 5, unit: 'sp' },
    weight: 1,
    capacity: '0.2 cubic foot / 6 pounds of gear',
  },
  {
    id: 'quiver',
    name: 'Quiver',
    category: 'container',
    cost: { amount: 1, unit: 'gp' },
    weight: 1,
    capacity: '20 arrows',
  },
  {
    id: 'ram-portable',
    name: 'Ram, Portable',
    category: 'standard-gear',
    cost: { amount: 4, unit: 'gp' },
    weight: 35,
    description: 'Grants +4 bonus on Strength checks to break open doors. One other character can assist for advantage.',
  },
  {
    id: 'rations',
    name: 'Rations (1 day)',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'sp' },
    weight: 2,
    description: 'Dry foods suitable for travel, including jerky, dried fruit, hardtack, and nuts.',
  },
  {
    id: 'robes',
    name: 'Robes',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 4,
  },
  {
    id: 'rope-hempen',
    name: 'Rope, Hempen (50 feet)',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 10,
    description: 'Has 2 hit points and can be burst with a DC 17 Strength check.',
  },
  {
    id: 'rope-silk',
    name: 'Rope, Silk (50 feet)',
    category: 'standard-gear',
    cost: { amount: 10, unit: 'gp' },
    weight: 5,
    description: 'Has 2 hit points and can be burst with a DC 17 Strength check.',
  },
  {
    id: 'sack',
    name: 'Sack',
    category: 'container',
    cost: { amount: 1, unit: 'cp' },
    weight: 0.5,
    capacity: '1 cubic foot / 30 pounds of gear',
  },
  {
    id: 'scale-merchants',
    name: "Scale, Merchant's",
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 3,
    description: 'Includes a small balance, pans, and weights. Measures up to 2 pounds.',
  },
  {
    id: 'sealing-wax',
    name: 'Sealing Wax',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'sp' },
    weight: 0,
  },
  {
    id: 'shovel',
    name: 'Shovel',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 5,
  },
  {
    id: 'signal-whistle',
    name: 'Signal Whistle',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'cp' },
    weight: 0,
  },
  {
    id: 'signet-ring',
    name: 'Signet Ring',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'gp' },
    weight: 0,
  },
  {
    id: 'soap',
    name: 'Soap',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'cp' },
    weight: 0,
  },
  {
    id: 'spellbook',
    name: 'Spellbook',
    category: 'standard-gear',
    cost: { amount: 50, unit: 'gp' },
    weight: 3,
    description: 'Essential for wizards, this leather-bound book has 100 blank vellum pages.',
  },
  {
    id: 'spikes-iron',
    name: 'Spikes, Iron (10)',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'gp' },
    weight: 5,
    quantity: 10,
  },
  {
    id: 'spyglass',
    name: 'Spyglass',
    category: 'standard-gear',
    cost: { amount: 1000, unit: 'gp' },
    weight: 1,
    description: 'Objects viewed through a spyglass are magnified to twice their size.',
  },
  {
    id: 'tent-two-person',
    name: 'Tent, Two-Person',
    category: 'standard-gear',
    cost: { amount: 2, unit: 'gp' },
    weight: 20,
  },
  {
    id: 'tinderbox',
    name: 'Tinderbox',
    category: 'standard-gear',
    cost: { amount: 5, unit: 'sp' },
    weight: 1,
    description: 'Contains flint, fire steel, and tinder. Lighting a torch (or anything else with abundant fuel) takes an action.',
  },
  {
    id: 'torch',
    name: 'Torch',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'cp' },
    weight: 1,
    description: 'Burns for 1 hour, providing bright light in a 20-foot radius and dim light for an additional 20 feet.',
  },
  {
    id: 'vial',
    name: 'Vial',
    category: 'container',
    cost: { amount: 1, unit: 'gp' },
    weight: 0,
    capacity: '4 ounces',
  },
  {
    id: 'waterskin',
    name: 'Waterskin',
    category: 'container',
    cost: { amount: 2, unit: 'sp' },
    weight: 5,
    capacity: '4 pints',
    description: 'Full waterskin weighs 5 pounds.',
  },
  {
    id: 'whetstone',
    name: 'Whetstone',
    category: 'standard-gear',
    cost: { amount: 1, unit: 'cp' },
    weight: 1,
  },
];

/**
 * Get adventuring gear by ID
 */
export function getAdventuringGear(id: string): AdventuringGear | undefined {
  return adventuringGear.find((g) => g.id === id);
}

/**
 * Get all gear in a category
 */
export function getGearByCategory(category: GearCategory): AdventuringGear[] {
  return adventuringGear.filter((g) => g.category === category);
}

/**
 * Search gear by name
 */
export function searchGear(query: string): AdventuringGear[] {
  const normalized = query.toLowerCase().trim();
  return adventuringGear.filter(
    (g) =>
      g.name.toLowerCase().includes(normalized) ||
      g.id.includes(normalized)
  );
}
